import React, { useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';

import { allSiteCookies, siteCookiesDefaultValues } from './constants';
import { SiteCookie, SiteCookieFlag } from './types';

export type GasparCookiesProviderContextData = {
  shouldShowModal: boolean;
  cookies: Record<SiteCookie, boolean>;
  toggleCookie: (key: SiteCookie) => void;
  acceptSelectedCookies: () => void;
  declineAllCookies: () => void;
  deleteAllCookiesDevelopment: () => void;
};

export const GasparCookiesProviderContext = React.createContext<GasparCookiesProviderContextData | undefined>(undefined);
GasparCookiesProviderContext.displayName = 'GasparCookiesProviderContext';

type GasparCookiesProviderProps = { children?: React.ReactNode };
const GasparCookiesProvider: React.FC<GasparCookiesProviderProps> = ({ children }) => {
  // The persisted cookie state is stored in the browser's document.cookie object
  const [cookiesPersisted, setCookiesPersisted, removeCookiePersisted] = useCookies<SiteCookie | SiteCookieFlag>(allSiteCookies);

  // The cookies state is derived from the persisted cookie state and the default values
  const cookies = useMemo(() => {
    return Object.entries(siteCookiesDefaultValues).reduce((acc, [cookie, defaultValue]) => {
      if (cookie in cookiesPersisted) {
        return { ...acc, [cookie]: cookiesPersisted[cookie] == true };
      }
      return { ...acc, [cookie]: 'gaspar' in cookiesPersisted ? false : defaultValue };
    }, {} as Record<SiteCookie, boolean>);
  }, [cookiesPersisted]);

  // The modal should be shown if the `gaspar` cookie is not set
  const shouldShowModal = useMemo(() => !('gaspar' in cookiesPersisted), [cookiesPersisted]);

  const toggleCookie = useCallback(
    (cookie: SiteCookie) => {
      // If the cookie is already in the persisted state, remove it
      if (cookie in cookiesPersisted) {
        removeCookiePersisted(cookie);
        return;
      }
      if (!(cookie in cookiesPersisted)) {
        setCookiesPersisted(cookie, true);
        return;
      }
    },
    [cookiesPersisted, removeCookiePersisted, setCookiesPersisted],
  );

  // Helper function to accept all cookies
  const acceptSelectedCookies = useCallback(() => {
    Object.entries(siteCookiesDefaultValues).forEach(([cookie, shouldPersist]) => setCookiesPersisted(cookie as SiteCookie, shouldPersist));
    setCookiesPersisted('gaspar', true);
  }, [setCookiesPersisted]);

  // Helper function to decline all cookies
  const declineAllCookies = useCallback(() => {
    Object.keys(siteCookiesDefaultValues).forEach((cookie: SiteCookie) => removeCookiePersisted(cookie));
    setCookiesPersisted('gaspar', true);
  }, [removeCookiePersisted, setCookiesPersisted]);

  // Helper function to delete all cookies under the domain
  const deleteAllCookiesDevelopment = () => {
    const cookiesInDocument = document.cookie.split(';');
    cookiesInDocument.forEach((cookie) => {
      const eqPosition = cookie.indexOf('=');
      const name = eqPosition > -1 ? cookie.substring(0, eqPosition) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
    });
  };

  return (
    <GasparCookiesProviderContext.Provider
      value={{
        shouldShowModal,
        cookies,
        acceptSelectedCookies,
        declineAllCookies,
        deleteAllCookiesDevelopment,
        toggleCookie,
      }}
    >
      {children}
    </GasparCookiesProviderContext.Provider>
  );
};
GasparCookiesProvider.displayName = 'GasparCookiesProvider';

export default GasparCookiesProvider;

export const useGasparCookies = () => {
  const context = useContext(GasparCookiesProviderContext);
  if (!context) {
    throw Error(`Missing required context`);
  }
  return context;
};
